'use client';

// Panel de acciones del MÓDULO 4 (Juzgamiento). Se muestra dentro de la pestaña
// "Juzgamiento" del detalle de expediente cuando el estado está en fase de juzgamiento.
// Reutiliza el mismo patrón de "tarjeta de acción -> Dialog con formulario mínimo" de la
// pestaña de Instrucción. Cada acción refresca el detalle al terminar y muestra toasts.
// Los bloqueos procesales del backend llegan como error 422 con {message}; se muestran.

import { useEffect, useState } from 'react';
import {
  CalendarClock, ClipboardList, FileSignature, Gavel, Mic, Scale, Send, ShieldAlert, Split,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import { getCatalogos } from '@/lib/expedientes';
import {
  repartoJuzgamiento, definirTramite,
  ordinarioDisposicion, ordinarioDescargos, ordinarioResolverNulidad,
  ordinarioDecretarPruebas, ordinarioTrasladoAlegatos,
  verbalAutoVerbal, verbalAudiencia, verbalPracticaPruebas, verbalReanudarAlegatos, verbalLecturaFallo,
  variacionCargos, crearFallo, notificarFallo, interponerRecurso, concederRecurso,
  funcionariosJuzgamiento, idJuicio, tipoJuicioDe, tieneJuzgamiento,
  puedeRepartirJuzgamiento, puedeFallar, usuarioActual,
  SENTIDO_FALLO, TIPO_SANCION, MEDIO_AUDIENCIA,
  type UsuarioLite, type TipoJuicio, type SentidoFallo, type TipoSancion, type MedioAudiencia,
} from '@/lib/juzgamiento';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const hoy = () => new Date().toISOString().slice(0, 10);

// ---- Definición de campos y acciones (patrón compartido con Instrucción) ---

type CampoTipo = 'text' | 'date' | 'time' | 'textarea' | 'bool' | 'select';
interface Campo {
  name: string;
  label: string;
  tipo: CampoTipo;
  required?: boolean;
  opciones?: string[];
  placeholder?: string;
  defecto?: string | boolean;
  // Se muestra solo si la función devuelve true (según el resto de valores).
  visible?: (v: Record<string, string | boolean>) => boolean;
}

interface AccionDef {
  key: string;
  label: string;
  descripcion: string;
  icon: React.ReactNode;
  campos: Campo[];
  ejecutar: (exp: ExpDetalle, valores: Record<string, string | boolean>) => Promise<unknown>;
}

// ---- Acciones a nivel de expediente ---------------------------------------

function accionesTramite(): AccionDef[] {
  return [
    {
      key: 'definir-tramite', label: 'Definir trámite', icon: <Split className="size-4" />,
      descripcion: 'Decide si el juzgamiento se surte por juicio ORDINARIO o VERBAL.',
      campos: [
        { name: 'tipoJuicio', label: 'Tipo de juicio', tipo: 'select', required: true, opciones: ['ORDINARIO', 'VERBAL'], defecto: 'ORDINARIO' },
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'fundamento', label: 'Fundamento', tipo: 'textarea', required: true },
        { name: 'autoDefineKey', label: 'Auto que define (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => definirTramite(exp.id, {
        tipoJuicio: v.tipoJuicio as TipoJuicio,
        fecha: v.fecha as string,
        fundamento: v.fundamento as string,
        ...(v.autoDefineKey ? { autoDefineKey: v.autoDefineKey as string } : {}),
      }),
    },
  ];
}

// ---- Acciones de juicio ORDINARIO -----------------------------------------

function accionesOrdinario(): AccionDef[] {
  const jid = (exp: ExpDetalle) => idJuicio(exp, 'ORDINARIO');
  return [
    {
      key: 'ord-disposicion', label: 'Disposición (notificación)', icon: <Send className="size-4" />,
      descripcion: 'Registra la fecha de notificación de la disposición.',
      campos: [{ name: 'fechaNotificacion', label: 'Fecha de notificación', tipo: 'date', required: true, defecto: hoy() }],
      ejecutar: (exp, v) => ordinarioDisposicion(jid(exp), { fechaNotificacion: v.fechaNotificacion as string }),
    },
    {
      key: 'ord-descargos', label: 'Descargos', icon: <ClipboardList className="size-4" />,
      descripcion: 'Descargos ordinarios (Art. 225b, 15 días).',
      campos: [
        { name: 'presentoDescargos', label: 'Presentó descargos', tipo: 'bool' },
        { name: 'aportoPruebas', label: 'Aportó pruebas', tipo: 'bool' },
        { name: 'solicitoPruebas', label: 'Solicitó pruebas', tipo: 'bool' },
        { name: 'presentoNulidad', label: 'Presentó nulidad', tipo: 'bool' },
        { name: 'descargosKey', label: 'Documento descargos (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => ordinarioDescargos(jid(exp), {
        presentoDescargos: !!v.presentoDescargos,
        aportoPruebas: !!v.aportoPruebas,
        solicitoPruebas: !!v.solicitoPruebas,
        presentoNulidad: !!v.presentoNulidad,
        ...(v.descargosKey ? { descargosKey: v.descargosKey as string } : {}),
      }),
    },
    {
      key: 'ord-resolver-nulidad', label: 'Resolver nulidad', icon: <ShieldAlert className="size-4" />,
      descripcion: 'Resuelve la nulidad propuesta en descargos.',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'decision', label: 'Decisión', tipo: 'select', required: true, opciones: ['NIEGA', 'PARCIAL', 'TOTAL'], defecto: 'NIEGA' },
        { name: 'autoKey', label: 'Auto (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => ordinarioResolverNulidad(jid(exp), {
        fecha: v.fecha as string,
        decision: v.decision as 'NIEGA' | 'PARCIAL' | 'TOTAL',
        ...(v.autoKey ? { autoKey: v.autoKey as string } : {}),
      }),
    },
    {
      key: 'ord-decretar-pruebas', label: 'Decretar pruebas', icon: <ClipboardList className="size-4" />,
      descripcion: 'Auto que decreta pruebas (Art. 225c, 90 días calendario).',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'autoPruebasKey', label: 'Auto de pruebas (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => ordinarioDecretarPruebas(jid(exp), {
        fecha: v.fecha as string,
        ...(v.autoPruebasKey ? { autoPruebasKey: v.autoPruebasKey as string } : {}),
      }),
    },
    {
      key: 'ord-traslado-alegatos', label: 'Traslado para alegatos', icon: <Send className="size-4" />,
      descripcion: 'Traslado para alegatos de conclusión (10 días).',
      campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
      ejecutar: (exp, v) => ordinarioTrasladoAlegatos(jid(exp), { fecha: v.fecha as string }),
    },
  ];
}

// ---- Acciones de juicio VERBAL --------------------------------------------

function accionesVerbal(): AccionDef[] {
  const jid = (exp: ExpDetalle) => idJuicio(exp, 'VERBAL');
  return [
    {
      key: 'ver-auto-verbal', label: 'Auto verbal (cita a audiencia)', icon: <CalendarClock className="size-4" />,
      descripcion: 'Auto que ordena juicio verbal y cita a audiencia.',
      campos: [
        { name: 'fecha', label: 'Fecha del auto', tipo: 'date', required: true, defecto: hoy() },
        { name: 'fechaAudiencia', label: 'Fecha de audiencia', tipo: 'date', required: true },
        { name: 'horaAudiencia', label: 'Hora de audiencia', tipo: 'time', required: true },
        { name: 'medio', label: 'Medio', tipo: 'select', required: true, opciones: [...MEDIO_AUDIENCIA], defecto: 'PRESENCIAL' },
        { name: 'linkAudiencia', label: 'Enlace de audiencia (si virtual/mixta)', tipo: 'text', visible: (v) => v.medio !== 'PRESENCIAL' },
      ],
      ejecutar: (exp, v) => verbalAutoVerbal(jid(exp), {
        fecha: v.fecha as string,
        fechaAudiencia: v.fechaAudiencia as string,
        horaAudiencia: v.horaAudiencia as string,
        medio: v.medio as MedioAudiencia,
        ...(v.linkAudiencia ? { linkAudiencia: v.linkAudiencia as string } : {}),
      }),
    },
    {
      key: 'ver-audiencia', label: 'Registrar audiencia', icon: <Mic className="size-4" />,
      descripcion: 'Registra si la audiencia se celebró o se reprograma.',
      campos: [
        { name: 'celebrada', label: 'Audiencia celebrada', tipo: 'bool', defecto: true },
        { name: 'actaKey', label: 'Acta (key, opcional)', tipo: 'text', visible: (v) => !!v.celebrada },
        { name: 'motivoNoCelebracion', label: 'Motivo de no celebración', tipo: 'textarea', visible: (v) => !v.celebrada },
        { name: 'nuevaFecha', label: 'Nueva fecha', tipo: 'date', visible: (v) => !v.celebrada },
        { name: 'nuevaHora', label: 'Nueva hora', tipo: 'time', visible: (v) => !v.celebrada },
      ],
      ejecutar: (exp, v) => verbalAudiencia(jid(exp), {
        celebrada: !!v.celebrada,
        ...(v.actaKey ? { actaKey: v.actaKey as string } : {}),
        ...(v.motivoNoCelebracion ? { motivoNoCelebracion: v.motivoNoCelebracion as string } : {}),
        ...(v.nuevaFecha ? { nuevaFecha: v.nuevaFecha as string } : {}),
        ...(v.nuevaHora ? { nuevaHora: v.nuevaHora as string } : {}),
      }),
    },
    {
      key: 'ver-practica-pruebas', label: 'Práctica de pruebas', icon: <ClipboardList className="size-4" />,
      descripcion: 'Registra la práctica de pruebas en audiencia.',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'practicadas', label: 'Pruebas practicadas', tipo: 'bool', defecto: true },
      ],
      ejecutar: (exp, v) => verbalPracticaPruebas(jid(exp), {
        fecha: v.fecha as string,
        practicadas: !!v.practicadas,
      }),
    },
    {
      key: 'ver-reanudar-alegatos', label: 'Reanudar para alegatos', icon: <Send className="size-4" />,
      descripcion: 'Reanuda la audiencia para alegatos de conclusión.',
      campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
      ejecutar: (exp, v) => verbalReanudarAlegatos(jid(exp), { fecha: v.fecha as string }),
    },
    {
      key: 'ver-lectura-fallo', label: 'Lectura de fallo', icon: <Gavel className="size-4" />,
      descripcion: 'Registra la lectura del fallo en audiencia.',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'sentido', label: 'Sentido del fallo', tipo: 'select', required: true, opciones: [...SENTIDO_FALLO], defecto: 'SANCIONATORIO' },
        { name: 'falloKey', label: 'Fallo (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => verbalLecturaFallo(jid(exp), {
        fecha: v.fecha as string,
        sentido: v.sentido as SentidoFallo,
        ...(v.falloKey ? { falloKey: v.falloKey as string } : {}),
      }),
    },
  ];
}

// ---- Acciones comunes: variación, fallo, notificación, recursos -----------

function accionesComunes(tipo: TipoJuicio | null, autoridades: string[]): AccionDef[] {
  const tj = (): TipoJuicio => tipo ?? 'ORDINARIO';
  return [
    {
      key: 'variacion-cargos', label: 'Variación de cargos', icon: <ShieldAlert className="size-4" />,
      descripcion: 'Variación de cargos por error de calificación o por juzgamiento.',
      campos: [
        { name: 'tipoVariacion', label: 'Tipo de variación', tipo: 'select', required: true, opciones: ['ERROR_CALIFICACION', 'POR_JUZGAMIENTO'], defecto: 'ERROR_CALIFICACION' },
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'autoMotivadoKey', label: 'Auto motivado (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => variacionCargos(exp.id, {
        tipoJuicio: tj(),
        tipoVariacion: v.tipoVariacion as 'ERROR_CALIFICACION' | 'POR_JUZGAMIENTO',
        fecha: v.fecha as string,
        ...(v.autoMotivadoKey ? { autoMotivadoKey: v.autoMotivadoKey as string } : {}),
      }),
    },
    {
      key: 'fallo', label: 'Proferir fallo', icon: <Gavel className="size-4" />,
      descripcion: 'Fallo de primera instancia (bloquea 422 si faltan alegatos o hay nulidad pendiente).',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'sentido', label: 'Sentido del fallo', tipo: 'select', required: true, opciones: [...SENTIDO_FALLO], defecto: 'SANCIONATORIO' },
        { name: 'tipoSancion', label: 'Tipo de sanción (si sancionatorio/mixto)', tipo: 'select', opciones: [...TIPO_SANCION], visible: (v) => v.sentido === 'SANCIONATORIO' || v.sentido === 'MIXTO' },
        { name: 'observaciones', label: 'Observaciones (opcional)', tipo: 'textarea' },
        { name: 'falloKey', label: 'Fallo (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => crearFallo(exp.id, {
        fecha: v.fecha as string,
        sentido: v.sentido as SentidoFallo,
        ...(v.tipoSancion ? { tipoSancion: v.tipoSancion as TipoSancion } : {}),
        ...(v.observaciones ? { observaciones: v.observaciones as string } : {}),
        ...(v.falloKey ? { falloKey: v.falloKey as string } : {}),
      }),
    },
    {
      key: 'notificar-fallo', label: 'Notificar fallo', icon: <Send className="size-4" />,
      descripcion: 'Notifica el fallo (personal o por edicto).',
      campos: [
        { name: 'notificacionPersonal', label: 'Notificación personal', tipo: 'bool', defecto: true },
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'notificacionKey', label: 'Notificación (key, opcional)', tipo: 'text', visible: (v) => !!v.notificacionPersonal },
        { name: 'edictoKey', label: 'Edicto (key, opcional)', tipo: 'text', visible: (v) => !v.notificacionPersonal },
      ],
      ejecutar: (exp, v) => notificarFallo(idFallo(exp), {
        notificacionPersonal: !!v.notificacionPersonal,
        fecha: v.fecha as string,
        ...(v.notificacionKey ? { notificacionKey: v.notificacionKey as string } : {}),
        ...(v.edictoKey ? { edictoKey: v.edictoKey as string } : {}),
      }),
    },
    {
      key: 'interponer-recurso', label: 'Interponer recurso', icon: <FileSignature className="size-4" />,
      descripcion: 'Interposición de recurso de apelación (10 días).',
      campos: [
        { name: 'fechaInterposicion', label: 'Fecha de interposición', tipo: 'date', required: true, defecto: hoy() },
        { name: 'forma', label: 'Forma (opcional)', tipo: 'text', placeholder: 'Ej: ESCRITO' },
        { name: 'recursoKey', label: 'Recurso (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => interponerRecurso(exp.id, {
        tipoRecurso: 'APELACION',
        fechaInterposicion: v.fechaInterposicion as string,
        ...(v.forma ? { forma: v.forma as string } : {}),
        ...(v.recursoKey ? { recursoKey: v.recursoKey as string } : {}),
      }),
    },
    {
      key: 'conceder-recurso', label: 'Conceder recurso', icon: <Scale className="size-4" />,
      descripcion: 'Concede el recurso y remite a la autoridad de segunda instancia.',
      campos: [
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        {
          name: 'autoridadDestino', label: 'Autoridad de segunda instancia', tipo: 'select', required: true,
          opciones: autoridades.length ? autoridades : ['GOBERNADOR', 'ALCALDE', 'DIRECTOR_COMPETENTE', 'PROCURADURIA', 'OTRO'],
        },
        { name: 'autoridadDestinoOtro', label: 'Otra autoridad (especifique)', tipo: 'text', visible: (v) => v.autoridadDestino === 'OTRO' },
        { name: 'autoConcedeKey', label: 'Auto que concede (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, v) => concederRecurso(idRecurso(exp), {
        fecha: v.fecha as string,
        autoridadDestino: v.autoridadDestino as string,
        ...(v.autoridadDestinoOtro ? { autoridadDestinoOtro: v.autoridadDestinoOtro as string } : {}),
        ...(v.autoConcedeKey ? { autoConcedeKey: v.autoConcedeKey as string } : {}),
      }),
    },
  ];
}

// Ids de fallo/recurso desde el detalle (respaldo cuando el backend los anida).
function idFallo(exp: ExpDetalle): string {
  const raw = exp as unknown as Record<string, unknown>;
  for (const k of ['fallo', 'fallo_primera_instancia', 'falloPrimeraInstancia']) {
    const v = raw[k];
    if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
    if (typeof v === 'string' && v) return v;
  }
  const fallos = raw['fallos'];
  if (Array.isArray(fallos) && fallos.length) {
    const ult = fallos[fallos.length - 1] as { id?: unknown };
    if (ult?.id) return String(ult.id);
  }
  return '';
}

function idRecurso(exp: ExpDetalle): string {
  const raw = exp as unknown as Record<string, unknown>;
  for (const k of ['recurso', 'recursoInterpuesto', 'recurso_interpuesto']) {
    const v = raw[k];
    if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
    if (typeof v === 'string' && v) return v;
  }
  const recursos = raw['recursos'];
  if (Array.isArray(recursos) && recursos.length) {
    const ult = recursos[recursos.length - 1] as { id?: unknown };
    if (ult?.id) return String(ult.id);
  }
  return '';
}

// ---- Selección de acciones según estado -----------------------------------

function claves(e: string, ...k: string[]) {
  const s = (e || '').toUpperCase();
  return k.some((x) => s.includes(x));
}

// ===========================================================================
// Componente principal del panel
// ===========================================================================

export default function PanelJuzgamiento({ exp, onCambio }: { exp: ExpDetalle; onCambio: () => void }) {
  const [accion, setAccion] = useState<AccionDef | null>(null);
  const [reparto, setReparto] = useState(false);
  const [autoridades, setAutoridades] = useState<string[]>([]);

  const usuario = usuarioActual();
  const rol = usuario?.rol;
  const estado = exp.estado_actual || '';

  const tipo = tipoJuicioDe(exp);
  const yaRepartido = tieneJuzgamiento(exp);

  useEffect(() => {
    getCatalogos()
      .then((c) => setAutoridades(c.autoridadSegundaInstancia ?? []))
      .catch(() => setAutoridades([]));
  }, []);

  // Construye la lista de acciones disponibles según el estado y el tipo de juicio.
  const acciones: AccionDef[] = [];
  const esReparto = claves(estado, 'PENDIENTE_REPARTO_JUZGAMIENTO', 'REPARTO_JUZGAMIENTO');

  // Definir trámite: disponible tras repartir, mientras no haya juicio definido.
  if (yaRepartido && !tipo) acciones.push(...accionesTramite());

  if (tipo === 'ORDINARIO' || claves(estado, 'JUICIO_ORDINARIO')) acciones.push(...accionesOrdinario());
  if (tipo === 'VERBAL' || claves(estado, 'JUICIO_VERBAL')) acciones.push(...accionesVerbal());

  // Variación / fallo / recursos: en fases avanzadas del juzgamiento.
  if (
    yaRepartido ||
    claves(estado, 'JUICIO', 'VARIACION', 'ALEGATOS', 'FALLO', 'RECURSO')
  ) {
    acciones.push(...accionesComunes(tipo, autoridades));
  }

  const puedeReparto = puedeRepartirJuzgamiento(rol);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>Acciones de juzgamiento</CardTitle>
        {esReparto && puedeReparto && !yaRepartido && (
          <Button size="sm" onClick={() => setReparto(true)}>
            <Send className="size-4" /> Repartir a funcionario
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Acciones disponibles según el estado actual (<Badge variant="secondary">{estado}</Badge>)
          {tipo ? <> · Juicio <Badge variant="secondary">{tipo}</Badge></> : null}.
          Los bloqueos procesales del backend se muestran como error.
        </p>
        <Separator />
        {esReparto && !yaRepartido ? (
          <p className="text-sm text-muted-foreground">
            Expediente pendiente de reparto de juzgamiento.{' '}
            {puedeReparto
              ? 'Use "Repartir a funcionario" para asignarlo.'
              : 'Solo el Director de Juzgamiento puede repartirlo.'}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {acciones.map((a) => {
            const bloqueaFallo = (a.key === 'fallo' || a.key === 'ver-lectura-fallo') && !puedeFallar(rol);
            if (bloqueaFallo) return null;
            return (
              <button
                key={a.key}
                onClick={() => setAccion(a)}
                className="flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="flex items-center gap-2 text-sm font-medium">{a.icon} {a.label}</span>
                <span className="text-xs text-muted-foreground">{a.descripcion}</span>
              </button>
            );
          })}
          {acciones.length === 0 && !esReparto && (
            <p className="text-sm text-muted-foreground">No hay acciones de juzgamiento para este estado.</p>
          )}
        </div>
      </CardContent>

      {accion && (
        <ModalAccion
          accion={accion}
          exp={exp}
          onClose={() => setAccion(null)}
          onListo={() => { setAccion(null); onCambio(); }}
        />
      )}
      {reparto && (
        <ModalReparto
          exp={exp}
          onClose={() => setReparto(false)}
          onListo={() => { setReparto(false); onCambio(); }}
        />
      )}
    </Card>
  );
}

// ---- Modal genérico de acción (mismo patrón que Instrucción) --------------

function ModalAccion({ accion, exp, onClose, onListo }: {
  accion: AccionDef; exp: ExpDetalle; onClose: () => void; onListo: () => void;
}) {
  const [valores, setValores] = useState<Record<string, string | boolean>>(() => {
    const v: Record<string, string | boolean> = {};
    for (const c of accion.campos) {
      if (c.defecto !== undefined) v[c.name] = c.defecto;
      else if (c.tipo === 'bool') v[c.name] = false;
      else v[c.name] = '';
    }
    return v;
  });
  const [guardando, setGuardando] = useState(false);

  function set(name: string, val: string | boolean) {
    setValores((s) => ({ ...s, [name]: val }));
  }

  const visibles = accion.campos.filter((c) => !c.visible || c.visible(valores));

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    for (const c of visibles) {
      if (c.required && c.tipo !== 'bool') {
        if (!String(valores[c.name] ?? '').trim()) {
          toast.error(`El campo "${c.label}" es obligatorio.`);
          return;
        }
      }
    }
    setGuardando(true);
    try {
      await accion.ejecutar(exp, valores);
      toast.success(`${accion.label}: acción registrada.`);
      onListo();
    } catch (err) {
      // Incluye los bloqueos procesales 422 ({message}) que propaga api().
      toast.error('No se pudo completar la acción', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{accion.icon} {accion.label}</DialogTitle>
          <DialogDescription>{accion.descripcion}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          {visibles.map((c) => (
            <div key={c.name} className="space-y-2">
              {c.tipo === 'bool' ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!valores[c.name]}
                    onChange={(e) => set(c.name, e.target.checked)}
                    className="size-4 accent-primary"
                  />
                  {c.label}
                </label>
              ) : c.tipo === 'select' ? (
                <>
                  <Label>{c.label}</Label>
                  <Select value={String(valores[c.name] ?? '')} onValueChange={(v) => set(c.name, v)}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
                    <SelectContent>
                      {(c.opciones ?? []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              ) : c.tipo === 'textarea' ? (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Textarea id={c.name} rows={3} value={String(valores[c.name] ?? '')} onChange={(e) => set(c.name, e.target.value)} />
                </>
              ) : (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Input
                    id={c.name}
                    type={c.tipo === 'date' ? 'date' : c.tipo === 'time' ? 'time' : 'text'}
                    placeholder={c.placeholder}
                    value={String(valores[c.name] ?? '')}
                    onChange={(e) => set(c.name, e.target.value)}
                  />
                </>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Procesando…' : 'Confirmar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: reparto de juzgamiento ----------------------------------------

function ModalReparto({ exp, onClose, onListo }: {
  exp: ExpDetalle; onClose: () => void; onListo: () => void;
}) {
  const [funcionarios, setFuncionarios] = useState<UsuarioLite[]>([]);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [termino, setTermino] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    funcionariosJuzgamiento()
      .then(setFuncionarios)
      .catch((e) => toast.error('No se pudieron cargar los funcionarios', {
        description: e instanceof Error ? e.message : undefined,
      }));
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!funcionarioId) { toast.error('Seleccione un funcionario.'); return; }
    setGuardando(true);
    try {
      const body: { funcionarioId: string; terminoInterno?: string; observaciones?: string } = { funcionarioId };
      if (termino.trim()) body.terminoInterno = termino.trim();
      if (observaciones.trim()) body.observaciones = observaciones.trim();
      await repartoJuzgamiento(exp.id, body);
      toast.success(`Expediente ${exp.radicado} repartido a juzgamiento.`);
      onListo();
    } catch (err) {
      toast.error('No se pudo repartir el expediente', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="size-5 text-primary" /> Repartir a juzgamiento</DialogTitle>
          <DialogDescription>Radicado: <strong>{exp.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Funcionario de juzgamiento</Label>
            <Select value={funcionarioId} onValueChange={setFuncionarioId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
              <SelectContent>
                {funcionarios.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.nombre} {f.apellido} · {f.rol}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="termino">Término interno (opcional)</Label>
            <Input id="termino" value={termino} onChange={(e) => setTermino(e.target.value)} placeholder="Ej: 30 días" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="obs">Observaciones (opcional)</Label>
            <Textarea id="obs" rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Repartiendo…' : 'Repartir'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
